import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, Building2, ArrowRight, Navigation, ExternalLink } from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0  },
  viewport:    { once: true, amount: 0.1 },
  transition:  { duration: 0.52, ease: "easeOut", delay },
});

const fadeLeft = (delay = 0) => ({
  initial:     { opacity: 0, x: -28 },
  whileInView: { opacity: 1, x: 0   },
  viewport:    { once: true, amount: 0.1 },
  transition:  { duration: 0.52, ease: "easeOut", delay },
});

const fadeRight = (delay = 0) => ({
  initial:     { opacity: 0, x: 28 },
  whileInView: { opacity: 1, x: 0  },
  viewport:    { once: true, amount: 0.1 },
  transition:  { duration: 0.52, ease: "easeOut", delay },
});

const CONTACT_ITEMS = [
  {
    icon: MapPin,
    label: "Address",
    value: "Rukmini Knowledge Park, Kattigenahalli, Yelahanka, Bengaluru – 560064",
    link: "https://maps.google.com/?q=REVA+University+Bengaluru",
    linkLabel: "Open in Maps",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91-80-4696 6966",
    link: "tel:+918046966966",
    linkLabel: "Call Now",
  },
  {
    icon: Mail,
    label: "Conference Email",
    value: "ruceconference@reva.edu.in",
    link: "mailto:ruceconference@reva.edu.in",
    linkLabel: "Send Email",
  },
  {
    icon: Globe,
    label: "Website",
    value: "www.reva.edu.in",
    link: "https://www.reva.edu.in",
    linkLabel: "Visit Site",
  },
];

const VENUE_HIGHLIGHTS = [
  { label: "Campus Area",    value: "43 Acres",       sub: "Sprawling green campus"     },
  { label: "Location",       value: "Yelahanka",      sub: "North Bengaluru"            },
  { label: "Conference Mode",value: "Offline Only",   sub: "In-person attendance"       },
  { label: "Organised by",   value: "SOCE",           sub: "School of Civil Engineering"},
];

export default function VenueSection() {
  return (
    <section className="bg-gray-50">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-12 py-20 lg:py-28">

        {/* ── Section Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <motion.div {...fadeUp(0.05)} className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#F58220]" />
              <span className="text-[#F58220] text-[11px] font-bold tracking-[0.22em] uppercase">
                Conference Venue
              </span>
            </motion.div>
            <motion.h2
              {...fadeUp(0.1)}
              className="text-gray-900 text-3xl lg:text-[42px] font-black leading-tight tracking-tight"
            >
              Venue &amp;
              <span className="text-[#F58220]"> Location</span>
            </motion.h2>
            <motion.p {...fadeUp(0.16)} className="text-gray-500 text-[15px] mt-3 max-w-xl leading-relaxed">
              SMPACE-2026 will be held at REVA University's state-of-the-art campus
              in the heart of North Bengaluru — India's technology capital.
            </motion.p>
          </div>

          {/* Date badge */}
          <motion.div {...fadeUp(0.2)} className="flex-shrink-0">
            <div className="inline-flex items-center gap-3 border border-gray-200 rounded-2xl px-6 py-4 bg-gray-50 shadow-sm">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
                <Navigation size={16} className="text-[#F58220]" />
              </div>
              <div>
                <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase">Conference Dates</p>
                <p className="text-gray-900 text-[15px] font-black mt-0.5">July 31 – Aug 01, 2026</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Main Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

          {/* LEFT — Map + Campus Image */}
          <motion.div {...fadeLeft(0.1)} className="flex flex-col gap-5">

            {/* Google Maps Embed */}
            <div className="relative rounded-2xl overflow-hidden border border-gray-100 shadow-md">
              <div className="absolute top-4 left-4 z-10 bg-white rounded-xl px-3 py-2 shadow-md flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#F58220] animate-pulse" />
                <span className="text-gray-800 text-[12px] font-bold">REVA University</span>
              </div>
              <iframe
                title="REVA University Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.0!2d77.5900!3d13.1200!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae19b0b7c5e1c3%3A0x4c3e5d2f6a7b8e9d!2sREVA%20University!5e0!3m2!1sen!2sin!4v1698000000000!5m2!1sen!2sin"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
            </div>

            {/* Campus Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-md">
              <img
                src="https://images.unsplash.com/photo-1562774053-701939374585?w=900&q=80&auto=format&fit=crop"
                alt="REVA University Campus"
                className="w-full h-[220px] object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/70 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="text-white/70 text-[11px] font-bold tracking-widest uppercase">Campus</p>
                  <p className="text-white text-[16px] font-black mt-0.5">REVA University</p>
                  <p className="text-white/60 text-[12px]">Rukmini Knowledge Park, Bengaluru</p>
                </div>
                <a
                  href="https://maps.google.com/?q=REVA+University+Bengaluru"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 bg-[#F58220] text-white text-[11px] font-bold px-3 py-2 rounded-lg hover:bg-[#d96d12] transition-colors flex-shrink-0"
                >
                  <ExternalLink size={12} />
                  Directions
                </a>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — Info */}
          <motion.div {...fadeRight(0.15)} className="flex flex-col gap-5">

            {/* Venue name card */}
            <div className="bg-[#111827] rounded-2xl px-7 py-7 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#F58220]/8 blur-2xl pointer-events-none" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#F58220]/15 flex items-center justify-center">
                    <Building2 size={18} className="text-[#F58220]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-[10px] font-bold tracking-widest uppercase">Venue</p>
                    <p className="text-white text-[16px] font-black">REVA University</p>
                  </div>
                </div>
                <p className="text-white/60 text-[13px] leading-relaxed">
                  Rukmini Knowledge Park, Kattigenahalli,<br />
                  Yelahanka, Bengaluru – 560064<br />
                  Karnataka, India
                </p>
                <div className="flex items-center gap-3 mt-5">
                  <a
                    href="https://maps.google.com/?q=REVA+University+Bengaluru"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[12px] font-bold px-4 py-2.5 rounded-lg transition-all duration-200 group"
                  >
                    <MapPin size={13} />
                    Get Directions
                    <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                  </a>
                  <a
                    href="https://www.reva.edu.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white text-[12px] font-semibold px-4 py-2.5 rounded-lg transition-all duration-200"
                  >
                    <Globe size={13} />
                    Visit Website
                  </a>
                </div>
              </div>
            </div>

            {/* Venue highlight chips */}
            <div className="grid grid-cols-2 gap-4">
              {VENUE_HIGHLIGHTS.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.07, duration: 0.4 }}
                  className="bg-white border border-gray-100 rounded-xl px-5 py-4 shadow-sm hover:border-orange-200 hover:bg-orange-50/30 transition-all duration-200"
                >
                  <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-1">{h.label}</p>
                  <p className="text-gray-900 text-[16px] font-black leading-tight">{h.value}</p>
                  <p className="text-gray-400 text-[11px] mt-0.5">{h.sub}</p>
                </motion.div>
              ))}
            </div>

            {/* Contact info list */}
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
              <div className="px-6 py-4 border-b border-gray-100">
                <p className="text-gray-900 text-[14px] font-black">Contact Information</p>
              </div>
              <div className="divide-y divide-gray-50">
                {CONTACT_ITEMS.map(({ icon: Icon, label, value, link, linkLabel }, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 + i * 0.07, duration: 0.4 }}
                    className="flex items-start gap-4 px-6 py-4 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#F58220] transition-colors duration-200">
                      <Icon size={14} className="text-[#F58220] group-hover:text-white transition-colors duration-200" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-0.5">{label}</p>
                      <p className="text-gray-800 text-[13px] font-semibold break-all leading-snug">{value}</p>
                    </div>
                    <a
                      href={link}
                      target={link.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="flex-shrink-0 text-[11px] font-bold text-[#F58220] hover:underline flex items-center gap-1 mt-1"
                    >
                      {linkLabel}
                      <ArrowRight size={11} />
                    </a>
                  </motion.div>
                ))}
              </div>
            </div>

          </motion.div>
        </div>

        {/* ── Organised By Strip ── */}
        <motion.div
          {...fadeUp(0.25)}
          className="mt-10 bg-gray-50 border border-gray-100 rounded-2xl px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5"
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0">
              <Building2 size={20} className="text-[#F58220]" />
            </div>
            <div>
              <p className="text-gray-400 text-[10px] font-bold tracking-widest uppercase mb-0.5">Organised by</p>
              <p className="text-gray-900 text-[15px] font-black leading-tight">
                School of Civil Engineering, REVA University
              </p>
              <p className="text-gray-500 text-[12px] mt-0.5">
                Under the Rukmini Educational Charitable Trust, Bengaluru
              </p>
            </div>
          </div>
          <a
            href="https://www.reva.edu.in"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d96d12] text-white text-[13px] font-bold px-5 py-2.5 rounded-lg transition-all duration-200 group flex-shrink-0"
          >
            Visit REVA University
            <ExternalLink size={13} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </motion.div>

      </div>
    </section>
  );
}